import { useEffect, useRef, useState, useCallback } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowDown } from "lucide-react";
import { format, isToday, isYesterday, isSameDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { MessageBubble } from "./message-bubble";
import { MessageInput } from "./message-input";
import { InboxHeader } from "./inbox-header";
import type { InboxMessage } from "@/types/inbox-message";
import type { Inbox } from "@/types/inbox";

interface MessageThreadProps {
  inboxId: number;
  inbox?: Inbox;
  messages: InboxMessage[];
  loading: boolean;
  onMessageSent: () => void;
  onToggleDetail?: () => void;
  onStatusChange?: (status: "open" | "pending" | "resolved") => void;
}

function formatDateSeparator(date: Date): string {
  if (isToday(date)) return "Hoje";
  if (isYesterday(date)) return "Ontem";
  return format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
}

export function MessageThread({
  inboxId,
  inbox,
  messages,
  loading,
  onMessageSent,
  onToggleDetail,
  onStatusChange,
}: MessageThreadProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const handleScroll = useCallback(() => {
    const el = scrollAreaRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]",
    );
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollButton(distanceFromBottom > 200);
  }, []);

  useEffect(() => {
    const el = scrollAreaRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]",
    );
    if (!el) return;
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const sorted = [...messages].sort(
    (a, b) =>
      new Date(a.datetime_add).getTime() - new Date(b.datetime_add).getTime(),
  );

  const renderMessages = () => {
    const elements: React.ReactNode[] = [];
    let lastDate: Date | null = null;
    let lastDirection: string | null = null;

    for (let i = 0; i < sorted.length; i++) {
      const msg = sorted[i];
      const msgDate = new Date(msg.datetime_add);

      if (!lastDate || !isSameDay(lastDate, msgDate)) {
        elements.push(
          <div
            key={`date-${msg.datetime_add}`}
            className="flex items-center justify-center py-3"
          >
            <span className="rounded-full bg-muted px-3 py-1 text-[11px] font-medium text-muted-foreground">
              {formatDateSeparator(msgDate)}
            </span>
          </div>,
        );
        lastDirection = null;
      }

      const isGrouped =
        lastDirection === msg.direction &&
        lastDate &&
        isSameDay(lastDate, msgDate) &&
        msg.source !== "system";

      elements.push(
        <MessageBubble key={msg.id} message={msg} isGrouped={!!isGrouped} />,
      );

      lastDate = msgDate;
      lastDirection = msg.direction;
    }

    return elements;
  };

  return (
    <div className="flex h-full flex-col">
      <InboxHeader
        inboxId={inboxId}
        inbox={inbox}
        onToggleDetail={onToggleDetail}
        onStatusChange={onStatusChange}
      />

      <div className="relative flex-1">
        <ScrollArea className="h-full p-4" ref={scrollAreaRef}>
          {loading && messages.length === 0 ? (
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className={`flex ${i % 2 === 0 ? "justify-start" : "justify-end"}`}
                >
                  <Skeleton
                    className={`h-12 rounded-2xl ${i % 2 === 0 ? "w-2/3" : "w-1/2"}`}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-0">
              {renderMessages()}
              <div ref={bottomRef} />
            </div>
          )}
        </ScrollArea>

        {showScrollButton && (
          <Button
            size="icon"
            variant="secondary"
            className="absolute bottom-4 right-4 z-10 h-8 w-8 rounded-full shadow-md"
            onClick={scrollToBottom}
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
        )}
      </div>

      <MessageInput inboxId={inboxId} onSent={onMessageSent} />
    </div>
  );
}
